import React from "react";
import { Star, ShieldCheck, Clock, CheckCircle2, ArrowRight, Phone, MapPin, Truck, Award } from "lucide-react";
import { Button } from "./ui/button";
import { useQuoteModal } from "@/hooks/useQuoteModal";

export const HeroSection: React.FC = () => {
  const { openModal } = useQuoteModal();
  return (
    <section aria-label="Hero — Junk Removal Services Davie FL" className="relative bg-slate-950 text-white overflow-hidden">
      {/* Background Image with Dark Gradient Overlay */}
      <div className="absolute inset-0 z-0">
        <picture>
          <source 
            media="(max-width: 640px)" 
            srcSet="https://vibe.filesafe.space/1786557593842074307/assets/2241e093-a00f-4e31-9e23-6b347d9e9221.png?w=480&q=70 480w, https://vibe.filesafe.space/1786557593842074307/assets/2241e093-a00f-4e31-9e23-6b347d9e9221.png?w=640&q=75 640w"
            sizes="100vw"
          />
          <img
            src="https://vibe.filesafe.space/1786557593842074307/assets/2241e093-a00f-4e31-9e23-6b347d9e9221.png?w=800&q=75"
            alt="Muscle Junk Removal Davie FL truck and crew ready for heavy hauling"
            className="w-full h-full object-cover opacity-30 object-center"
            loading="eager"
            decoding="sync"
            fetchPriority="high"
            width={1200}
            height={675}
          />
        </picture>
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/90 to-slate-950/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-slate-950/50" />
      </div>

      {/* Decorative Glow Elements */}
      <div className="absolute top-1/4 left-10 w-96 h-96 bg-primary/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-10 right-10 w-80 h-80 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-20 lg:pt-16 lg:pb-28">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          
          {/* Left Hero Content */}
          <div className="lg:col-span-7 flex flex-col items-start">
            
            {/* Top Local SEO Badges */}
            <div className="flex flex-wrap items-center gap-2 mb-6">
              <span className="inline-flex items-center gap-1.5 bg-amber-400 text-slate-950 font-black px-3 py-1 rounded-full text-xs uppercase tracking-wide shadow-md">
                <MapPin className="w-3.5 h-3.5 fill-slate-950" /> Davie, FL Local Haulers
              </span>
              <span className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur border border-white/20 text-slate-100 px-3 py-1 rounded-full text-xs font-bold">
                <Clock className="w-3.5 h-3.5 text-amber-400" /> Same-Day Service
              </span>
              <span className="inline-flex items-center gap-1.5 bg-white/10 backdrop-blur border border-white/20 text-slate-100 px-3 py-1 rounded-full text-xs font-bold">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" /> Licensed &amp; Insured
              </span>
            </div>

            {/* Main SEO Headline */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black font-heading leading-none uppercase tracking-tight mb-6">
              <span className="text-amber-400">MUSCLE</span>{" "}
              <span className="text-primary">JUNK REMOVAL</span> <br />
              <span className="text-white text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-normal">
                DAVIE, FLORIDA
              </span>
            </h1>
            <p className="text-sm sm:text-base text-amber-400 font-bold uppercase tracking-widest mb-3">
              #1 Junk Removal Services in Davie & Broward County
            </p>

            {/* Subheadline with targeted local keywords */}
            <p className="text-lg sm:text-xl md:text-2xl font-bold text-slate-200 mb-4 max-w-2xl leading-snug">
              Davie’s Strongest Junk Hauling Team — We Do All The Heavy Lifting, Loading &amp; Cleanout So You Don’t Have To.
            </p>

            <p className="text-slate-300 text-base sm:text-lg mb-8 max-w-2xl">
              Serving Davie, Fort Lauderdale, Hollywood, Plantation, Cooper City, Sunrise & all of Broward County for 5+ years. From single furniture removal to full house, garage, and commercial cleanouts!
            </p>

            {/* CTA Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto mb-8">
              <Button
                  size="lg"
                  onClick={openModal}
                  className="w-full sm:w-auto h-14 px-8 text-lg font-black uppercase tracking-wider bg-primary hover:bg-rose-700 text-white shadow-xl shadow-rose-600/30 group"
                >
                  <span>Get Free Fast Quote</span>
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              <a href="tel:9542808090" className="w-full sm:w-auto">
                <Button
                  size="lg"
                  className="w-full sm:w-auto h-14 px-8 text-lg font-black uppercase tracking-wider bg-amber-400 hover:bg-amber-500 text-slate-950 shadow-xl shadow-amber-500/30"
                >
                  <Phone className="w-5 h-5 mr-2 shrink-0" />
                  <span>Call (954) 280-8090</span>
                </Button>
              </a>
            </div>

            {/* Google Review Social Proof Widget */}
            <div className="p-4 bg-slate-900/80 backdrop-blur border border-slate-800 rounded-2xl flex flex-wrap items-center gap-4 max-w-2xl">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <div className="flex flex-col">
                <span className="font-extrabold text-white text-sm">
                  5.0 Stars on Google
                </span>
                <span className="text-xs text-slate-400">
                  Trusted by home &amp; business owners across Davie, FL &amp; Broward County
                </span>
              </div>
              <div className="ml-auto bg-slate-800 border border-slate-700 px-3 py-1 rounded-lg text-xs font-bold text-amber-400">
                Google Verified
              </div>
            </div>

          </div>

          {/* Right Hero Image Card & Quick Trust Badges */}
          <div className="lg:col-span-5">
            <div className="relative rounded-3xl overflow-hidden border-2 border-slate-800 bg-slate-900 shadow-2xl group">
              <picture>
                <source 
                  media="(max-width: 640px)" 
                  srcSet="https://vibe.filesafe.space/1786557593842074307/assets/27b05448-6dd1-4711-b328-ad3ccc656c97.png?w=360&q=70 360w, https://vibe.filesafe.space/1786557593842074307/assets/27b05448-6dd1-4711-b328-ad3ccc656c97.png?w=480&q=75 480w" 
                  sizes="(max-width: 640px) 100vw, 50vw"
                />
                <img
                  src="https://vibe.filesafe.space/1786557593842074307/assets/27b05448-6dd1-4711-b328-ad3ccc656c97.png?w=400&q=75"
                  alt="Muscle Junk Removal crew team in Davie Florida loading junk onto professional dump trailer"
                  className="w-full h-[360px] sm:h-[420px] object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="eager"
                  decoding="async"
                  fetchPriority="high"
                  width={500}
                  height={350}
                />
              </picture>
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent opacity-90" />

              {/* Floating Badge on Image */}
              <div className="absolute bottom-4 left-4 right-4 p-4 bg-slate-900/90 backdrop-blur-md rounded-2xl border border-amber-400/30">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs font-bold text-amber-400 uppercase tracking-wider">
                      Davie Florida's #1 Choice
                    </div>
                    <div className="text-lg font-black text-white font-heading">
                      100% Upfront Pricing • No Surprise Fees
                    </div>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center border border-primary/40 shrink-0">
                    <Truck className="w-5 h-5 text-primary" />
                  </div>
                </div>
              </div>
            </div>

            {/* Guarantee Grid under image */}
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div className="bg-slate-900/80 border border-slate-800 p-3 rounded-xl flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-amber-400 shrink-0" />
                <span className="text-xs font-bold text-slate-200">Free On-Site Quotes</span>
              </div>
              <div className="bg-slate-900/80 border border-slate-800 p-3 rounded-xl flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-amber-400 shrink-0" />
                <span className="text-xs font-bold text-slate-200">Eco-Friendly Recycling</span>
              </div>
              <div className="bg-slate-900/80 border border-slate-800 p-3 rounded-xl flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-amber-400 shrink-0" />
                <span className="text-xs font-bold text-slate-200">Heavy Lifting Crew</span>
              </div>
              <div className="bg-slate-900/80 border border-slate-800 p-3 rounded-xl flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-400 shrink-0" />
                <span className="text-xs font-bold text-slate-200">5+ Years Davie Local</span>
              </div>
            </div>

          </div>

        </div>
      </div>

      {/* Trust banner stripe directly below Hero */}
      <div className="bg-primary text-white py-3 border-y border-rose-700/50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 flex flex-wrap items-center justify-center sm:justify-around gap-2 sm:gap-4 text-[10px] sm:text-xs md:text-sm font-black uppercase tracking-wider text-center">
          <span className="flex items-center gap-1.5 whitespace-nowrap">
            <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-300 shrink-0" /> 5+ Years Serving Davie
          </span>
          <span className="flex items-center gap-1.5 whitespace-nowrap">
            <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-300 shrink-0" /> Family Owned & Operated
          </span>
          <span className="flex items-center gap-1.5 whitespace-nowrap">
            <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-300 shrink-0" /> Licensed & Insured
          </span>
          <span className="flex items-center gap-1.5 whitespace-nowrap">
            <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-300 shrink-0" /> Same-Day Hauling Available
          </span>
          <span className="flex items-center gap-1.5 whitespace-nowrap">
            <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-300 shrink-0" /> 100% Upfront Pricing
          </span>
        </div>
      </div>
    </section>
  );
};
